import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Box, Grid, LinearProgress } from '@mui/material';
import { LayoutDashboard } from '../../../shared/layouts';
import { GenericService } from '../../../shared/services';
import { AccessRoleDTO, AccessRoleFilterDTO, ApplicationDTO, ApplicationFilterDTO, ValueListDTO } from '../../../shared/interfaces';
import { ISnackbarAlert, SnackbarAlert, CardList, TextFieldInput, ToolBar, IDialogActions, ICardActions, ICardColumns, IChipFilter, SelectField, CheckBoxComp, IChipsSettingProps } from '../../../shared/components';
import { ApplicationMaintenance } from './ApplicationMaintenance';
import { Delete } from '../../../shared/components/confirm-delete/ConfirmDelete';
import { ListId, LocalStorange } from '../../../shared/environment';
import { Feature } from '../feature/Feature';

export const Application: React.FC = () => {

    const [typeList, setTypeList] = useState<ValueListDTO[]>([]);
    const [applicationList, setApplicationList] = useState<ApplicationDTO[]>([]);
    const [applicationFilterDTO, setApplicationFilterDTO] = useState<ApplicationFilterDTO>();

    const [actionExecuted, setActionExecuted] = useState(false);
    const [newRecord, setNewRecord] = useState('');
    const [editRecord, setEditRecord] = useState('');
    const [deleteRecord, setDeleteRecord] = useState('');
    const [expadedData, setExpandedData] = useState('');
    const [isOpenMaintenance, setIsOpenMaintenance] = useState(false);

    const [loading, setLoading] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState<ISnackbarAlert>();
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);

    const [accessRolePermissionDTO, setAccessRolePermissionDTO] = useState<AccessRoleDTO>();
    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');

    useEffect(() => {
        setApplicationFilterDTO({
            ...applicationFilterDTO,
            name: '',
            typeId: '',
            isLocal: true,
            companyId: '',
        } as ApplicationFilterDTO);
    }, []);

    useEffect(() => {
        handleApplicationGetAllViewAsync();
        handleListGetAll(ListId.appsphere.application.typeId, setTypeList);

        handleGetPermission(ListId.appsphere.applicationId, setAccessRolePermissionDTO);
    }, [applicationFilterDTO]);

    useEffect(() => {
        if (actionExecuted)
            handleApplicationGetAllViewAsync();
    }, [actionExecuted]);

    const handleGetPermission = useCallback(async (featureId: string, setList: any) => {
        const accessRoleFilterDTO: AccessRoleFilterDTO = {
            featureId: featureId,
            userId: userInfo.user.id,
        };
        GenericService.PostGenericAsync1('authsphere.AccessRole', 'GetPermissionAsync',accessRoleFilterDTO, setActionExecuted, setLoading, undefined,  setList);
    }, []);

    const handleListGetAll = useCallback(async (listId: string, setList: any) => {
        const valueListFilterDTO = {
            value: '',
            listId: listId,
            orderBy: 'value ASC'
        };
        GenericService.GetAllAsync1(
            'parmsphere.valueList',
            valueListFilterDTO,
            setLoading,
            setList,
            showMessage
        );
    }, []);

    const handleApplicationGetAllViewAsync = useCallback(async () => {
        if (applicationFilterDTO === undefined)
            return;

        if(accessRolePermissionDTO?.allowGetAllView == undefined)
            return;
        if(!accessRolePermissionDTO?.allowGetAllView) {
            showMessage('There is no permission for get all view.', 'info');
            return;
        }

        GenericService.GetAllViewAsync1('appsphere.application', applicationFilterDTO, setActionExecuted, setLoading, setApplicationList, showMessage, true);
    }, [applicationFilterDTO, accessRolePermissionDTO]);

    const handleClearFilterClick = () => {
        setApplicationFilterDTO({
            ...applicationFilterDTO,
            name: '',
            typeId: '',
            isLocal: false,
            companyId: '',
        } as ApplicationFilterDTO);
    };

    function handleSearchClick() {
        handleApplicationGetAllViewAsync();
    }

    const handleNew = () => {
        if(!accessRolePermissionDTO?.allowAdd) {
            showMessage('There is no permission for new record.', 'info');
            return;
        }
        setNewRecord('data');
        setEditRecord('');
        setDeleteRecord('');
        setIsOpenMaintenance(true);
    };

    const handleViewDetatils = (data: string) => {
        if(!accessRolePermissionDTO?.allowGetById) {
            showMessage('There is no permission for view details.', 'info');
            return;
        }
        setNewRecord('');
        setEditRecord(data);
        setDeleteRecord('');
        setIsOpenMaintenance(true);
    };

    const handleDelete = (data: string) => {
        if(!accessRolePermissionDTO?.allowDelete) {
            showMessage('There is no permission for delete record.', 'info');
            return;
        }
        setNewRecord('');
        setDeleteRecord(data);
        setEditRecord('');
        setIsOpenMaintenance(true);
    };

    const toolBarActions: IDialogActions[] = [
        { id: 'new', text: 'New Application', icon: 'add_circle_outlined', iconColor: 'primary', setAction: handleNew },
    ];

    const cardActions: ICardActions[] = [
        { id: 'viewDetails', toolTip: 'View Details', icon: 'preview_outlined', iconColor: 'primary', setAction: handleViewDetatils },
        { id: 'delete', toolTip: 'Delete', icon: 'delete_outlined', iconColor: 'error', setAction: handleDelete },
    ];

    const chipFilters: IChipFilter[] = [
        { label: 'Total Records', value: applicationList.length },
        { label: 'Name', value: applicationFilterDTO?.name },
        { label: 'Type', value: typeList.find(x => x.id === applicationFilterDTO?.typeId)?.value },
    ];

    const chipStatus : IChipsSettingProps[] = [
        { value: 'true', iconName: 'task_alt_outlined', iconColor: 'warning' },
        { value: 'false', iconName: 'block_outlined', iconColor: 'info' },
    ];

    const columns: ICardColumns[] = [
        { field: 'name', label: 'Name', size: 4, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'type', label: 'Type', size: 1, type: 'text', visible: true, isChip: true, hasFieldLine: false, hasToolTip: true },
        { field: 'url', label: 'URL', size: 4, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'isLocal', label: 'Is Local', size: 1, type: 'boolean', visible: true, isChip: true, hasFieldLine: false, hasToolTip: true, chipSettings: chipStatus},
        { field: 'company', label: 'Company', size: 2, type: 'text', visible: true, isChip: true, hasFieldLine: false, hasToolTip: true },
    ];

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarMessage(msg);
        setIsOpenSnackBar(true);
    }

    return (
        <React.Fragment>
            {isOpenSnackBar && (
                <SnackbarAlert
                    snackBarAlert={snackBarMessage}
                    setIsOpen={setIsOpenSnackBar}
                    isOpen={isOpenSnackBar}
                />
            )}
            <LayoutDashboard title='APPLICATION'>
                <ToolBar
                    clickSearch={handleSearchClick}
                    clickClearFilter={handleClearFilterClick}
                    chipFilters={chipFilters}
                    actions={toolBarActions}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={3}>
                            <TextFieldInput
                                id='name'
                                label='Name'
                                setValue={setApplicationFilterDTO}
                                value={applicationFilterDTO?.name}
                                obj={applicationFilterDTO}
                                placeholder={'Name'}
                                maxLength={200}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='typeId'
                                label='Type'
                                setValue={setApplicationFilterDTO}
                                value={applicationFilterDTO?.typeId}
                                obj={applicationFilterDTO}
                                valueList={typeList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <CheckBoxComp
                                id='isLocal'
                                label='Local Application'
                                setValue={setApplicationFilterDTO}
                                objDTO={applicationFilterDTO}
                                defaultChecked={applicationFilterDTO?.isLocal}
                                disabled={false}
                            />
                        </Grid>
                    </Grid>
                </ToolBar>
                {loading && (
                    <LinearProgress sx={{ borderRadius: 3, mt: - 0.7, mb: 0.2 }} />
                )}
                <Box sx={{ display: 'flex', maxHeight: '79vh', width: '100%', overflow: 'hidden', overflowY: 'auto' }}>
                    <CardList
                        columns={columns}
                        rows={applicationList}
                        actions={cardActions}
                        hasChildren={true}
                        setRowExpanded={setExpandedData}
                        cardSize={12}
                        childrenTitle={'Associate Features'}
                        childrenComponent={(
                            <Box width={'100%'}>
                                <Feature data={expadedData} />
                            </Box>
                        )}
                    />
                </Box>
                {newRecord && (
                    <ApplicationMaintenance
                        data={''}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {editRecord && (
                    <ApplicationMaintenance
                        data={editRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {deleteRecord && (<Delete
                    entity={'appsphere.application'}
                    data={deleteRecord}
                    setIsOpenDiaglog={setIsOpenMaintenance}
                    isOpenDialog={isOpenMaintenance}
                    setActionExecuted={setActionExecuted}
                />
                )}
            </LayoutDashboard>
        </React.Fragment >
    );
};
